import type { GeometryElement as JXGElement } from 'jsxgraph';
import type { BoundLabel } from '@examdojo/core/widget';
import type { PortMap, PointPort } from '../../../port';
import type { GeometryPadElementContext, CreatedGeometryElement } from '../../element.model';
import { FEATURE_LINE_DEFAULTS } from '../feature.defaults';
import type { DiagonalElement } from './diagonal.model';

/**
 * Create diagonal lines for a quadrilateral shape.
 *
 * Reads the parent shape's vertex ports (A..D) and draws one or both
 * diagonals as dashed lines. Exposes length and intersection ports.
 */
export function createDiagonal(
  element: DiagonalElement & { id: string },
  context: GeometryPadElementContext,
): CreatedGeometryElement {
  const { elementFactory, portRegistry, displayConfig } = context;
  const jsxObjects: JXGElement[] = [];
  const labels: BoundLabel[] = [];

  const parentId = element.params.of;

  // Per-element display overrides
  const elementDisplay = displayConfig.elements?.[element.id];

  // Resolve vertex getters from the parent shape
  const getA = portRegistry.getGetter(`${parentId}.point.A`);
  const getB = portRegistry.getGetter(`${parentId}.point.B`);
  const getC = portRegistry.getGetter(`${parentId}.point.C`);
  const getD = portRegistry.getGetter(`${parentId}.point.D`);

  const lineStyle = {
    color: elementDisplay?.line?.color ?? FEATURE_LINE_DEFAULTS.color,
    strokeWidth: elementDisplay?.line?.strokeWidth ?? FEATURE_LINE_DEFAULTS.strokeWidth,
    dash: elementDisplay?.line?.dash ?? FEATURE_LINE_DEFAULTS.dash,
    visible: elementDisplay?.visible ?? true,
    label: elementDisplay?.line?.label ?? (false as const),
    ...(elementDisplay?.line ?? {}),
    vertex: { fixed: true },
  };

  // Determine which diagonals to draw
  const drawBoth = !element.params.vertices;
  const drawD1 =
    drawBoth ||
    (element.params.vertices![0] === 'A' && element.params.vertices![1] === 'C') ||
    (element.params.vertices![0] === 'C' && element.params.vertices![1] === 'A');
  const drawD2 =
    drawBoth ||
    (element.params.vertices![0] === 'B' && element.params.vertices![1] === 'D') ||
    (element.params.vertices![0] === 'D' && element.params.vertices![1] === 'B');

  // --- Diagonal 1: A → C ---
  if (drawD1) {
    const p0 = getA() as PointPort;
    const p2 = getC() as PointPort;
    const line1 = elementFactory.line({
      a: p0,
      b: p2,
      name: `${element.id}-d1`,
      style: lineStyle,
    });
    jsxObjects.push(line1.line, line1.a, line1.b);
  }

  // --- Diagonal 2: B → D ---
  if (drawD2) {
    const p1 = getB() as PointPort;
    const p3 = getD() as PointPort;
    const line2 = elementFactory.line({
      a: p1,
      b: p3,
      name: `${element.id}-d2`,
      style: lineStyle,
    });
    jsxObjects.push(line2.line, line2.a, line2.b);
  }

  // --- Build ports ---
  const ports: PortMap = {};

  // Length port for diagonal A-C
  if (drawD1) {
    ports['d1'] = () => {
      const a = getA() as PointPort;
      const b = getC() as PointPort;
      return dist(a, b);
    };
  }

  // Length port for diagonal B-D
  if (drawD2) {
    ports['d2'] = () => {
      const a = getB() as PointPort;
      const b = getD() as PointPort;
      return dist(a, b);
    };
  }

  // Intersection port: line-line intersection of the two diagonals
  if (drawD1 && drawD2) {
    ports['intersection'] = () => {
      const a = getA() as PointPort;
      const b = getC() as PointPort;
      const c = getB() as PointPort;
      const d = getD() as PointPort;
      return lineLineIntersection(a, b, c, d);
    };
  }

  return {
    jsxObjects,
    labels,
    ports,
    destroy: () => {
      const board = elementFactory.getContext().board;
      for (const label of labels) {
        board.removeObject(label);
      }
      for (const obj of jsxObjects) {
        board.removeObject(obj);
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Math helpers
// ---------------------------------------------------------------------------

function dist(a: PointPort, b: PointPort): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Compute the intersection of two line segments (a→b) and (c→d).
 *
 * Uses the parametric form:
 *   P = a + t*(b - a)
 *   Q = c + s*(d - c)
 *
 * Solving for t:
 *   t = ((c - a) × (d - c)) / ((b - a) × (d - c))
 * where × denotes the 2D cross product (ux*vy - uy*vx).
 */
function lineLineIntersection(a: PointPort, b: PointPort, c: PointPort, d: PointPort): PointPort {
  const bax = b[0] - a[0];
  const bay = b[1] - a[1];
  const dcx = d[0] - c[0];
  const dcy = d[1] - c[1];
  const cax = c[0] - a[0];
  const cay = c[1] - a[1];

  const denom = bax * dcy - bay * dcx;

  // If lines are parallel (denom ≈ 0), fall back to midpoint of segment a→b
  if (Math.abs(denom) < 1e-12) {
    return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  }

  const t = (cax * dcy - cay * dcx) / denom;
  return [a[0] + t * bax, a[1] + t * bay];
}
